import {PrimaryMigrationObject} from 'config-migration/types/MigrationObjects';

export interface TemplateDetails {
  templateCollectionId: string;
}
export interface MigrationObjectChange extends PrimaryMigrationObject {
  objectApiIdentifier: string;
  objectAction: MigrationChangeType;
  diffDetails: Array<MigrationDiff>;
}

// Keep in sync with config_migration/diff_engine/primitives.py
export enum SegmentType {
  REPEATED_INDEX_LABEL = 'REPEATED_INDEX_LABEL',
  REPEATED_STRING_LABEL = 'REPEATED_STRING_LABEL',
  FIELD_LABEL = 'FIELD_LABEL',
}

export interface PathSegment {
  segmentName: string;
  segmentType: SegmentType;
}

export enum MigrationChangeType {
  ADDITION = 'ADDITION',
  NO_CHANGE = 'NO_CHANGE',
  SUBTRACTION = 'SUBTRACTION',
  UPDATE = 'UPDATE',
}

// Keep in sync with config_migration/diff_engine/formatters.py
/** This controls how detailed the diff is rendered in the change log UI. */
export enum MigrationChangelogRenderType {
  FULL = 'FULL',
  MASK_DETAILS = 'MASK_DETAILS',
  ARCHIVE = 'ARCHIVE',
  UNARCHIVE = 'UNARCHIVE',
}

// Keep in sync with config_migration/diff_engine/primitives.py
/** An enum representing the type of change that occurred in a diff. */
export enum ChangelogVerb {
  CREATE = 'Create',
  REMOVE = 'Remove',
  UPDATE = 'Update',
  ARCHIVE = 'Archive',
  UNARCHIVE = 'Unarchive',
  RENAME = 'Rename',
  /** This is used for boolean fields that are being updated */
  MARK = 'Mark',
}

export const CHANGELOG_VERB_PRESENT_TENSE: {[changelogVerb in ChangelogVerb]: string} = {
  [ChangelogVerb.CREATE]: 'Creating',
  [ChangelogVerb.REMOVE]: 'Removing',
  [ChangelogVerb.UPDATE]: 'Updating',
  [ChangelogVerb.ARCHIVE]: 'Archiving',
  [ChangelogVerb.UNARCHIVE]: 'Unarchiving',
  [ChangelogVerb.RENAME]: 'Renaming',
  [ChangelogVerb.MARK]: 'Marking',
};

export const CHANGELOG_VERB_PAST_TENSE: {[changelogVerb in ChangelogVerb]: string} = {
  [ChangelogVerb.CREATE]: 'Created',
  [ChangelogVerb.REMOVE]: 'Removed',
  [ChangelogVerb.UPDATE]: 'Updated',
  [ChangelogVerb.ARCHIVE]: 'Archived',
  [ChangelogVerb.UNARCHIVE]: 'Unarchived',
  [ChangelogVerb.RENAME]: 'Renamed',
  [ChangelogVerb.MARK]: 'Marked',
};

export const CONSTRAINT_VERB_TO_DISPLAY_TEXT: {[diffType in MigrationChangeType]: string} = {
  [MigrationChangeType.ADDITION]: 'added',
  [MigrationChangeType.SUBTRACTION]: 'removed',
  [MigrationChangeType.UPDATE]: 'updated',
  [MigrationChangeType.NO_CHANGE]: 'no change',
};

export const DIFF_TYPE_TO_VERB_PRESENT: {[diffType: string]: string} = {
  ADDITION: CHANGELOG_VERB_PRESENT_TENSE[ChangelogVerb.CREATE],
  SUBTRACTION: CHANGELOG_VERB_PRESENT_TENSE[ChangelogVerb.REMOVE],
  UPDATE: CHANGELOG_VERB_PRESENT_TENSE[ChangelogVerb.UPDATE],
};

export const DIFF_TYPE_TO_VERB_PAST: {[diffType: string]: string} = {
  ADDITION: CHANGELOG_VERB_PAST_TENSE[ChangelogVerb.CREATE],
  SUBTRACTION: CHANGELOG_VERB_PAST_TENSE[ChangelogVerb.REMOVE],
  UPDATE: CHANGELOG_VERB_PAST_TENSE[ChangelogVerb.UPDATE],
};

/** Field names that should be hidden from the changelog UI (internal implementation details). */
export const CHANGELOG_HIDDEN_FIELD_NAMES = new Set(['provenance_uri']);

interface MigrationAddition {
  diffType: MigrationChangeType.ADDITION;
  diffPath: Array<PathSegment>;
  renderType: MigrationChangelogRenderType;
  objectClass: string;
  objectDisplayName: string | null;
  changelogVerb: ChangelogVerb;
}

export interface MigrationSubtraction {
  diffType: MigrationChangeType.SUBTRACTION;
  diffPath: Array<PathSegment>;
  renderType: MigrationChangelogRenderType;
  objectClass: string;
  objectDisplayName: string | null;
  provenanceUri: string;
  changelogVerb: ChangelogVerb;
}

export interface MigrationUpdate {
  diffType: MigrationChangeType.UPDATE;
  diffPath: Array<PathSegment>;
  renderType: MigrationChangelogRenderType;
  leftValue: string;
  rightValue: string;
  provenanceUri: string;
  changelogVerb: ChangelogVerb;
}

export type MigrationDiff = MigrationAddition | MigrationSubtraction | MigrationUpdate;
